import axios, { AxiosInstance } from 'axios';
import type {
  CancelledResponse,
  CreateCheckoutParams,
  CustomerDetailsData,
  MakePaymentData,
  PlansSuccessResponse,
  UpdateBsoOptionsData,
  VerifyPlanResponse,
  PlanAction,
  CustomerResource,
  GetCustomerResource,
  ApplicationStep,
  ApprovalResponse,
  AcceptLoanPurposeSuccessResponse,
  CustomerDetailSuccessResponse,
  GetPaymentMethodsResponse,
  PlanResponse,
  PreApprovalCheckResponse,
  SubmitBankSatementsRequest,
  SubmitBankSatementsResponse,
  SubmitHouseholdExpensesRequest,
  SubmitHouseholdExpensesResponse,
  PlanApprovalCheckResponse,
  AcceptDepositBumpingSuccessResponse,
  RatesSuccessResponse,
  StoreSuccessResponse,
  CustomerSearchResponse,
  CustomerDeclaredIncomeExpenseData,
  CustomerIncomeExpenseData,
  CloudCheckRequest,
  PaymentGatewayDetails,
  MakePaymentResponse,
  PaymentMethodsResponse,
  UpdateCustomerData,
  CheckoutUpdateSuccessResponse,
  CheckoutResource,
  LoginSuccessResponse,
  LoginMfaRequiredResponse,
  ApplicationCompletedBy,
  CreatePaymentMethodRequest,
  CreatePaymentMethodResponse,
  SubmitBankSatementsPdfUploadRequest,
} from '@payright/types-bnpl-checkout-api';

export class BnplCheckoutApi {
  axiosInstance: AxiosInstance;

  constructor() {
    const url = `${process.env.REACT_APP_BNPL_CHECKOUT_API_URL}/au/bnpl/api/v1`;
    this.axiosInstance = axios.create({
      baseURL: url,
      timeout: 80000,
      headers: {
        common: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
    });

    this.axiosInstance.interceptors.response.use((response) => {
      return response.data;
    });
  }

  login = async (
    username: string,
    password: string,
    request_id?: string,
    code?: string
  ): Promise<LoginSuccessResponse | LoginMfaRequiredResponse> => {
    try {
      const url = `/customer/login`;
      const response = await this.axiosInstance.post<
        LoginSuccessResponse | LoginMfaRequiredResponse
      >(
        url,
        {
          ...(request_id !== undefined && { request_id }),
          ...(code !== undefined && { code }),
        },
        {
          auth: {
            username,
            password,
          },
        }
      );

      if ('mfa_required' in response.data) {
        return {
          message: response.data.message,
          mfa_required: response.data.mfa_required,
          requestId: response.data.requestId,
          phoneNumberHint: response.data.phoneNumberHint,
        };
      }

      if ('auth_token' in response.data && response.data.auth_token && response.data.customer_id) {
        return {
          auth_token: response.data.auth_token,
          customer_id: response.data.customer_id,
        };
      }

      throw new Error('Unexpected server response. Please try again.');
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.message);
      }
      throw error;
    }
  };

  loginApplicant = async (username: string, password: string): Promise<LoginSuccessResponse> => {
    try {
      const url = `/customer/applicant`;
      const response = await this.axiosInstance.post<any>(url, { username, password });
      if (response.data.auth_token && response.data.entity_id) {
        return {
          auth_token: response.data.auth_token,
          customer_id: response.data.entity_id,
        };
      } else {
        throw new Error('Unexpected server response. Please try again.');
      }
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.message);
      }
      throw error;
    }
  };

  forgotPassword = async (userName: string): Promise<any> => {
    try {
      const url = `/customer/forgot-password`;
      const response = await this.axiosInstance.post<any>(url, {
        userName,
      });
      if (response.data.requestId) {
        return {
          message: 'Password has been reset.',
        };
      } else {
        throw new Error('Unexpected server response. Please try again.');
      }
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.message);
      }
      throw error;
    }
  };

  changePassword = async (
    authToken: string,
    customerId: string,
    tempPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<any> => {
    try {
      const url = `/customer/change-password`;
      const response = await this.axiosInstance.put<any>(
        url,
        {
          customerId,
          tempPassword,
          newPassword,
          confirmPassword,
        },
        {
          headers: {
            'auth-token': authToken,
          },
        }
      );
      if (response.data) {
        return {
          message: 'Password has been changed.',
        };
      } else {
        throw new Error('Unexpected server response. Please try again.');
      }
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.message);
      }
      throw error;
    }
  };

  rates = async (checkoutId: string): Promise<RatesSuccessResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/rates`;
      const response = await this.axiosInstance.get<RatesSuccessResponse>(url);
      if (response.data) {
        return {
          ...response.data,
        };
      } else {
        throw new Error('Unable to fetch the rates.');
      }
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.message);
      }
      throw error;
    }
  };

  store = async (checkoutId: string): Promise<StoreSuccessResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/store`;
      const response = await this.axiosInstance.get<StoreSuccessResponse>(url);
      if (response.data) {
        return {
          ...response.data,
        };
      } else {
        throw new Error('Unable to fetch store.');
      }
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.message);
      }
      throw error;
    }
  };

  customerSearch = async (
    checkoutId: string,
    customerDetails: Required<
      Pick<CustomerDetailsData, 'firstName' | 'lastName' | 'middleName' | 'dateOfBirth'>
    > &
      Partial<Pick<CustomerDetailsData, 'email' | 'phone'>>,
    loanAmount: number
  ): Promise<CustomerSearchResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/customer`;
      const response = await this.axiosInstance.get<CustomerSearchResponse>(url, {
        params: {
          firstName: customerDetails.firstName,
          lastName: customerDetails.lastName,
          middleName: customerDetails.middleName,
          dateOfBirth: customerDetails.dateOfBirth,
          email: customerDetails.email,
          phoneMobile: customerDetails.phone,
          productLine: 'BNPL',
          loanAmount,
        },
      });
      return response.data;
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.message);
      }
      throw error;
    }
  };

  getCurrentCheckout = async (checkoutId: string): Promise<any> => {
    try {
      const url = `/checkouts/${checkoutId}`;
      const response = await this.axiosInstance.get(url);

      let data = { ...response.data };

      // In Update checkout, payment details does not have the depositPercentage value at step 1 for the return customer
      // We are storing that depositPercenatge into the DepositPaidPercentage for the merchant-api
      // For the temporary fix we are reverting the below object and sending it to merchant-api
      // We need to fix this mismatching variable
      data.attributes.paymentDetails = {
        saleAmount: data.attributes.saleAmount,
        depositAmount: data.attributes.depositPayable,
        depositPercent: data.attributes.depositPaidPercentage,
        paymentFrequency: data.attributes.terms,
        paymentPeriod: data.attributes.paymentPeriod,
        establishmentFee: data.attributes.paymentDetails.establishmentFee,
        showAllAvailableTerms: data.attributes.showAllAvailableTerms,
      };

      return data;
    } catch (error) {
      throw new Error(error.message);
    }
  };

  getCurrentCheckoutItems = async (checkoutId: string): Promise<any> => {
    try {
      const url = `/checkouts/${checkoutId}/items`;
      const response = await this.axiosInstance.get(url);

      let data = { ...response.data };

      // Return as converted JSON object to Array
      return Object.keys(data).map((key) => data[key]);
    } catch (error) {
      throw new Error(error.message);
    }
  };

  getCustomerById = async (
    checkoutId: string,
    customerId: string
  ): Promise<GetCustomerResource> => {
    const url = `/checkouts/${checkoutId}/customer/${customerId}`;
    try {
      const response = await this.axiosInstance.get<GetCustomerResource>(url);
      return response.data;
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  };

  updateCheckout = async (
    checkoutId: string,
    checkoutParams: CreateCheckoutParams
  ): Promise<CheckoutUpdateSuccessResponse> => {
    try {
      const url = `/checkouts/${checkoutId}`;
      const response = await this.axiosInstance.put<CheckoutUpdateSuccessResponse>(
        url,
        checkoutParams
      );

      if (response.data) {
        return response.data;
      } else {
        throw new Error('Unable to update the checkout.');
      }
    } catch (error) {
      throw error;
    }
  };

  customerCreation = async (checkoutId: string, customerDetailsDataSet: any): Promise<any> => {
    try {
      const url = `/checkouts/${checkoutId}/customer`;
      const response = await this.axiosInstance.post(url, customerDetailsDataSet);
      if (response.data.id) {
        return {
          id: response.data.id,
          auth_token: response.data.auth_token,
          email: response.data.customerDetails.email,
          fundingSource: response.data.fundingSource,
        };
      } else {
        throw new Error('Unable to create customer record  .');
      }
    } catch (error) {
      throw error;
    }
  };

  updateCustomer = async (
    checkoutId: string,
    customerId: string,
    // ToDo: Add type
    customerResource: CustomerResource
  ): Promise<CustomerResource> => {
    try {
      const url = `/checkouts/${checkoutId}/customer/${customerId}`;
      const response = await this.axiosInstance.put(url, customerResource);
      if (response.data) {
        return response.data;
      } else {
        throw new Error('Unable to update customer');
      }
    } catch (error) {
      throw error;
    }
  };

  submitHouseholdExpenses = async (
    checkoutId: string,
    customerId: string,
    request: SubmitHouseholdExpensesRequest
  ): Promise<SubmitHouseholdExpensesResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/customer/${customerId}/household-expenses`;
      const response = await this.axiosInstance.post(url, request);
      if (response.data) {
        return response.data;
      } else {
        throw new Error("Unable to update customer's household expenses");
      }
    } catch (error) {
      throw error;
    }
  };

  submitBankStatements = async (
    checkoutId: string,
    customerId: string,
    request: SubmitBankSatementsRequest
  ): Promise<SubmitBankSatementsResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/customer/${customerId}/bank-statements`;
      return await this.axiosInstance.post<SubmitBankSatementsRequest, SubmitBankSatementsResponse>(
        url,
        request
      );
    } catch (error) {
      const meaningfulErrorMessage = error.response?.data?.error?.message;

      if (meaningfulErrorMessage) {
        throw new Error(meaningfulErrorMessage);
      }

      throw error;
    }
  };

  submitBankStatementsPdfUpload = async (
    checkoutId: string,
    customerId: string,
    request: SubmitBankSatementsPdfUploadRequest
  ): Promise<SubmitBankSatementsResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/customer/${customerId}/bank-statements-pdf-upload`;

      const formData = new FormData();

      (request.fileToUpload as File[]).forEach((file) => {
        // Explicitly set the filename to prevent browsers (especially Firefox on Android)
        // from appending metadata like ";size=125328" to the filename
        formData.append('fileToUpload', file, file.name);
      });

      return await this.axiosInstance.post<
        SubmitBankSatementsPdfUploadRequest,
        SubmitBankSatementsResponse
      >(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      const meaningfulErrorMessage = error.response?.data?.error?.message;

      if (meaningfulErrorMessage) {
        throw new Error(meaningfulErrorMessage);
      }

      throw error;
    }
  };

  customerToCheckout = async (
    customerId: string,
    checkoutId: string,
    applicationCompletedBy: ApplicationCompletedBy
  ): Promise<CheckoutResource> => {
    try {
      const url = `/checkouts/${checkoutId}/customer/${customerId} `;
      const applicationProcessedBy = {
        applicationCompletedBy: applicationCompletedBy,
      };
      const response = await this.axiosInstance.post<CheckoutResource>(url, applicationProcessedBy);
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          `${error.response?.status} - ${error.response.statusText}: ${error.response.data.error.message}`
        );
      }

      throw new Error(error.message);
    }
  };

  getCheckoutCustomer = async (checkoutId: string): Promise<CustomerResource> => {
    try {
      const url = `/checkouts/${checkoutId}/customer`;
      const response = await this.axiosInstance.get<CustomerResource>(url);
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  };

  runPlanPreApprovalCheck = async (
    checkoutId: string,
    isCheckoutUpdated: boolean,
    isExistingCustomer: boolean
  ): Promise<PreApprovalCheckResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/pre-approval-check?isCheckoutUpdated=${isCheckoutUpdated}&isExistingCustomer=${isExistingCustomer}`;
      const response = await this.axiosInstance.get(url);
      return response.data;
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  };

  runAcceptPreApprovalCheck = async (
    checkoutId: string,
    depositAmount: number,
    depositPercentage: number
  ): Promise<AcceptDepositBumpingSuccessResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/accept-pre-approval`;
      const response = await this.axiosInstance.patch(url, {
        depositAmount,
        depositPercentage,
      });
      return response.data;
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  };

  updatedPlans = async (
    action: PlanAction,
    checkoutId: string,
    planId: string
  ): Promise<PlansSuccessResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/plans/${planId}`;
      const response = await this.axiosInstance.put<PlansSuccessResponse>(url, {
        action,
      });
      if (response.data) {
        return {
          ...response.data,
        };
      } else {
        throw new Error('Unable to activate the plans');
      }
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.message);
      }
      throw error;
    }
  };

  verifyPlanSubmission = async (
    planId: string,
    checkoutId: string,
    verificationCode: string
  ): Promise<VerifyPlanResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/plans/${planId}/verify/${verificationCode}`;
      const response = await this.axiosInstance.get(url);
      return response.data;
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  };

  planApproval = async (checkoutId: string): Promise<ApprovalResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/plan/approve`;
      const response = await this.axiosInstance.post<ApprovalResponse>(url);
      return {
        result: response.data.result,
        nextStep: response.data.nextStep,
      };
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  };

  planApprovalCheck = async (checkoutId: string): Promise<PlanApprovalCheckResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/plan/approve/check`;
      const response = await this.axiosInstance.get(url);
      return {
        outcome: response.data.outcome,
        messageCode: response.data.messageCode,
      };
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  };

  getPlan = async (checkoutId: string, planId: string): Promise<PlanResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/plans/${planId}`;
      const response = await this.axiosInstance.get(url);
      return response.data;
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  };

  planCancellation = async (checkoutId: string): Promise<CancelledResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/cancel`;
      const response = await this.axiosInstance.put(url);

      return {
        cancelledStatus: response.data.status,
      };
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  };

  planConsent = async (checkoutId: string): Promise<ApplicationStep> => {
    try {
      const url = `/checkouts/${checkoutId}/plan/consent`;
      const response = await this.axiosInstance.post(url);
      return response.data.nextStep;
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  };

  getPaymentMethodsList = async (
    checkoutId: string,
    customerId: string
  ): Promise<GetPaymentMethodsResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/customer/${customerId}/payment-methods`;
      return await this.axiosInstance.get<{}, GetPaymentMethodsResponse>(url);
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.message);
      }
      throw error;
    }
  };

  getPaymentGatewayDetails = async (
    customerId: string,
    checkoutId: string
  ): Promise<PaymentGatewayDetails> => {
    try {
      const url = `/checkouts/${checkoutId}/customer/${customerId}/payment-gateway/${checkoutId}`;
      const response = await this.axiosInstance.get<PaymentGatewayDetails>(url);
      return response.data;
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.message);
      }
      throw error;
    }
  };

  addCardCloudPayments = async (
    checkoutId: string,
    customerId: string,
    cloudCheckRequest: CloudCheckRequest
  ): Promise<any> => {
    try {
      const url = `/checkouts/${checkoutId}/customer/${customerId}/payment-methods/Cloud_Payment`;
      const response = await this.axiosInstance.post(url, cloudCheckRequest);
      if (response.data) {
        return response.data;
      } else {
        throw new Error('Unable to add card');
      }
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.message);
      }
      throw error;
    }
  };

  addCard = async (
    checkoutId: string,
    customerId: string,
    paymentMethodData: CreatePaymentMethodRequest
  ): Promise<CreatePaymentMethodResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/customer/${customerId}/payment-methods/Eway`;

      const response = await this.axiosInstance.post<
        CreatePaymentMethodRequest,
        CreatePaymentMethodResponse
      >(url, paymentMethodData);
      if (response.success) {
        return response;
      } else {
        throw new Error('Unable to add card');
      }
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.response.data.error.message);
      }

      throw error;
    }
  };

  updateCustomerPaymentDetails = async (
    checkoutId: string,
    customerId: string,
    updateCustomerDataSet: UpdateCustomerData
  ): Promise<CustomerDetailSuccessResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/customer/${customerId}`;
      const response = await this.axiosInstance.put(url, updateCustomerDataSet);
      if (response.data) {
        return response.data;
      } else {
        throw new Error('Unable to update customer');
      }
    } catch (error) {
      throw error;
    }
  };

  makeCardActive = async (
    checkoutId: string,
    customerId: string,
    paymentMethodId: string
  ): Promise<PaymentMethodsResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/payment-methods/${paymentMethodId}`;
      const paymentMethodData = {
        customerId: customerId,
      };

      const response = await this.axiosInstance.put(url, paymentMethodData);
      if (response.data) {
        return response.data;
      } else {
        throw new Error('Unable to update card');
      }
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.response.data.error.message);
      }

      throw error;
    }
  };

  makePayment = async (
    checkoutId: string,
    paymentDataSet: MakePaymentData
  ): Promise<MakePaymentResponse> => {
    try {
      const url = `/checkouts/${checkoutId}/payment`;

      const response = await this.axiosInstance.post(url, paymentDataSet);
      if (response.data.status) {
        return response.data;
      } else {
        throw new Error('Unable to make payment');
      }
    } catch (error) {
      throw error;
    }
  };

  updateBsoOptions = async (
    checkoutId: string,
    provideBso: string
  ): Promise<UpdateBsoOptionsData> => {
    try {
      const url = `/checkouts/${checkoutId}/bso-options`;
      const response = await this.axiosInstance.patch(url, {
        provideBso,
      });

      return response.data;
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  };

  getCustomerIncomeExpense = async (
    checkoutId: string,
    customerId: string
  ): Promise<CustomerIncomeExpenseData> => {
    try {
      const url = `/checkouts/${checkoutId}/customer/${customerId}/monthly-income-expenses`;
      const response = await this.axiosInstance.get(url);

      return response.data;
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  };

  updateCustomerIncomeExpenses = async (
    checkoutId: string,
    customerId: string,
    customerDeclaredIncomeExpense: CustomerDeclaredIncomeExpenseData
  ): Promise<CustomerDeclaredIncomeExpenseData> => {
    try {
      const url = `/checkouts/${checkoutId}/customer/${customerId}/monthly-income-expenses`;
      const response = await this.axiosInstance.put(url, customerDeclaredIncomeExpense);
      return response.data;
    } catch (error) {
      if (error.isAxiosError) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  };

  acceptLoanPurpose = async (checkoutId: string): Promise<AcceptLoanPurposeSuccessResponse> => {
    const url = `/checkouts/${checkoutId}/plan/accept-loan-purpose`;
    const response = await this.axiosInstance.post(url);
    return response.data;
  };
}
